import React, { Fragment, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Link } from "react-router-dom";
import MainWrapper from "../../MainWrapper";
import { Table, Form, Row, Col, Button } from "react-bootstrap";
import Icon from "react-icons-kit";
import { ic_arrow_back } from "react-icons-kit/md";
import Loader from "../../layouts/Loader";
import axios from "axios";
import { API_URL } from "../../../config";

// Read-only view of already-acknowledged potential-lost-customer rows — the ones
// that drop off the alerts grid once acknowledged. Companion to the
// "Potential Lost Customers" drill-down (../dashboard/PotentialLostCustomers.jsx).
//
// GetAcknowledgements returns rows already joined to the property (no second
// call needed), newest first. Respects the role's dataScope ("self" => only the
// logged-in salesperson's properties), read from the role-permission payload the
// sidebar already loads.
const AcknowledgedList = ({
  auth: { auth, isAuthenticated },
  Admin: { userMenuList },
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Client-side filters. Property Name / Management Company are free-text
  // (case-insensitive "contains"); Salesperson / Reason are dropdowns built
  // from the distinct values present in the loaded rows.
  const [propertyFilter, setPropertyFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [salesPersonFilter, setSalesPersonFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");

  const token = auth && auth[0] ? auth[0].token : null;
  const userId = auth && auth[0] ? auth[0].userId : null;
  const roleName = auth && auth[0] ? auth[0]?.roleName : null;
  const dashboard =
    userMenuList && userMenuList[0] ? userMenuList[0].dashboard : null;
  const menuLoaded = !!(userMenuList && userMenuList[0]);
  const dataScope = (dashboard && dashboard.dataScope) || "all";
  let tmpSalesId = userId;
  if (auth && (roleName === "Sales" && auth[0]?.salesPersonId !== "")) {
    tmpSalesId = auth[0]?.salesPersonId;
  }
  const scopedSalesPersonId = dataScope === "self" ? tmpSalesId : null;

  const priorityStyle = { backgroundColor: "#ffa3a3", color: "#0D47A1" };

  useEffect(() => {
    if (!token) return;
    // Wait for the role-permission payload (userMenuList) before fetching.
    // dataScope is derived from it; fetching earlier fires a throwaway
    // unscoped call, then a second scoped call once the menu arrives.
    if (!menuLoaded) return;
    setLoading(true);

    // All 4 params are optional; only userId (and salesPersonId when scoped) are
    // sent. Backend already returns newest first.
    const params = {};
    if (roleName !== 'Admin') params.userId = userId;
    if (scopedSalesPersonId) params.salesPersonId = scopedSalesPersonId;

    axios
      .get(`${API_URL}/User/api/User/GetAcknowledgements`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      .then((res) => {
        const result = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setRows(result);
      })
      .catch((err) => {
        console.warn("Could not load acknowledged customers:", err.message);
      })
      .finally(() => setLoading(false));
  }, [token, menuLoaded, scopedSalesPersonId]);

  // Distinct, sorted options for the Salesperson / Reason dropdowns.
  const salesPersonOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => r.salesPersonName).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [rows]
  );
  const reasonOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => r.reasonName).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const property = propertyFilter.trim().toLowerCase();
    const company = companyFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (property && !(r.propertyName || "").toLowerCase().includes(property))
        return false;
      if (
        company &&
        !(r.managementCompany || "").toLowerCase().includes(company)
      )
        return false;
      if (salesPersonFilter && r.salesPersonName !== salesPersonFilter)
        return false;
      if (reasonFilter && r.reasonName !== reasonFilter) return false;
      return true;
    });
  }, [rows, propertyFilter, companyFilter, salesPersonFilter, reasonFilter]);

  const isFiltering =
    propertyFilter || companyFilter || salesPersonFilter || reasonFilter;

  const clearFilters = () => {
    setPropertyFilter("");
    setCompanyFilter("");
    setSalesPersonFilter("");
    setReasonFilter("");
  };

  if (!isAuthenticated) return <Navigate to="/login" />;

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();
    return `${month < 10 ? "0" + month : month}-${day < 10 ? "0" + day : day}-${year}`;
  };

  return (
    <Fragment>
      <MainWrapper>
        <Loader loading={loading}>
          <div className="container">
            <div className="sub-header mt-5 mb-3">
              <h2>Acknowledged Customers</h2>
              <Link className="btn btn-primary me-1" to="/dashboard">
                <Icon icon={ic_arrow_back} />Back
              </Link>
            </div>
            <Row className="g-2 mb-3 align-items-end">
              <Col md={3} sm={6}>
                <Form.Label className="mb-1">Property Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search property…"
                  value={propertyFilter}
                  onChange={(e) => setPropertyFilter(e.target.value)}
                />
              </Col>
              <Col md={3} sm={6}>
                <Form.Label className="mb-1">Management Company</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search company…"
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                />
              </Col>
              <Col md={2} sm={6}>
                <Form.Label className="mb-1">Salesperson</Form.Label>
                <Form.Select
                  value={salesPersonFilter}
                  onChange={(e) => setSalesPersonFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {salesPersonOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2} sm={6}>
                <Form.Label className="mb-1">Reason</Form.Label>
                <Form.Select
                  value={reasonFilter}
                  onChange={(e) => setReasonFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {reasonOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2} sm={6}>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={clearFilters}
                  disabled={!isFiltering}
                >
                  Clear Filters
                </Button>
              </Col>
            </Row>
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Property Name</th>
                  <th>Management Company</th>
                  <th>Salesperson</th>
                  <th>Reason</th>
                  <th>Notes</th>
                  <th>Last Order Date</th>
                  <th>Acknowledged On</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((row) => {
                    const rowStyle = row.priorityNumber ? { ...priorityStyle } : {};
                    return (
                      <tr key={row.ackId} style={rowStyle}>
                        <td style={rowStyle}>{row.propertyName || "—"} {row.storeName && <span className="print-hide badge bg-success rounded-pill p-1 badge-sm">{row.storeName}</span>}</td>
                        <td style={rowStyle}>{row.managementCompany || "—"}</td>
                        <td style={rowStyle}>{row.salesPersonName || "—"}</td>
                        <td style={rowStyle}>{row.reasonName || "—"}</td>
                        <td style={rowStyle}>{row.notes || "—"}</td>
                        <td style={rowStyle}>{formatDate(row.lastOrderDate)}</td>
                        {/*<td style={rowStyle}>
                          {row.daysSinceLastOrder != null
                            ? row.daysSinceLastOrder
                            : "—"}
                        </td>*/}
                        <td style={rowStyle}>{formatDate(row.acknowledgedOn)} {row.acknowledgedByName && <span className="print-hide badge bg-primary rounded-pill p-1 badge-sm">By - {row.acknowledgedByName}</span>}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      {isFiltering
                        ? "No acknowledged customers match the filters."
                        : "No acknowledged customers."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Loader>
      </MainWrapper>
    </Fragment>
  );
};

const mapStateToProps = ({ auth, Admin }) => ({ auth, Admin });

export default connect(mapStateToProps)(AcknowledgedList);
